import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiActivity, FiShield, FiZap, FiEye, FiServer, FiCloud, FiGlobe, FiArrowLeft } = FiIcons;

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4" />
            <span>Back to Status Checker</span>
          </Link>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            About Web Down Status
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            A privacy-focused, comprehensive website monitoring tool built for the modern web.
          </p>
        </motion.div>

        <div className="space-y-8">
          {/* Mission Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md"
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiActivity} className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              <span>Our Mission</span>
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Web Down Status was created to provide website owners, developers, and IT professionals with a 
              fast, reliable, and privacy-focused tool for monitoring website availability and performance. 
              We believe that website monitoring should be accessible to everyone without compromising on 
              privacy or requiring complex setup.
            </p>
          </motion.section>

          {/* Features Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md"
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Key Features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiZap} className="h-6 w-6 text-yellow-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Instant Results
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Get comprehensive status reports in seconds with detailed performance metrics.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiShield} className="h-6 w-6 text-green-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Privacy First
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    No data collection, no tracking, no cookies. Your checks remain completely private.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiServer} className="h-6 w-6 text-blue-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Deep Analysis
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Complete header analysis, security scoring, DNS resolution, and performance metrics.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiEye} className="h-6 w-6 text-purple-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Multi-Method Checking
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Multiple fallback methods ensure reliable results even when direct access is blocked.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* How It Works Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md"
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              How It Works
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiServer} className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    1. Server-Side Check (Primary)
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Our server performs a comprehensive analysis including full header extraction, 
                    security analysis, DNS resolution, and performance metrics.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCloud} className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    2. Proxy Check (Fallback)
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    If the server is unavailable, we use a Cloudflare Worker proxy to bypass CORS 
                    restrictions and provide basic status information.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiGlobe} className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    3. Limited Check (Final Fallback)
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    When all else fails, we perform DNS resolution and favicon checks to determine 
                    basic website availability.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Privacy Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6"
          >
            <h2 className="text-2xl font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="h-6 w-6" />
              <span>Privacy Commitment</span>
            </h2>
            <div className="space-y-3 text-green-800 dark:text-green-200">
              <p className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>No user data collection or storage</span>
              </p>
              <p className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>No tracking cookies or analytics</span>
              </p>
              <p className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>No user accounts or registration required</span>
              </p>
              <p className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>History stored locally in your browser only</span>
              </p>
              <p className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Open source and transparent</span>
              </p>
            </div>
          </motion.section>

          {/* Technology Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md"
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Built With Modern Technology
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">React</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Frontend</div>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">Node.js</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Backend</div>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">Cloudflare</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Proxy</div>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">DoH</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">DNS</div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default About;